import {
  ID,
  Profile,
  Address,
  Reward,
  Connection,
  OrderLedger,
  List,
  Order,
  ListItem,
  Interaction,
  TransactionLedger,
  Transaction,
  ProfileBalances,
  TransactionItem
} from '@omneo/omneo-sdk'
import { OmneoWishlistParser } from './omneoWishlistParser.js'
import { asyncTimeout } from '../helpers/asyncTimeout.js'
import { getRemainingDaysOnAssignment } from '../helpers/getRemainingDates.js'
/**
 * @typedef {import("../types/omneo.ts").createProfileConnection} createProfileConnection
 * @typedef {import("../types/omneo.ts").createProfileConnectionResponse} createProfileConnectionResponse
 * @typedef {import("../types/omneo.ts").OmneoStoreLocation} Location
 * @typedef {import("../types/page.ts").DashboardPage} DashboardPage
 */

/**
 *
 * OmneoProfileClient
 *
 * Is used only for managing the base profiles details and using
 * methods that are not available on the Omneo Client.
 *
 */
export class OmneoProfileClient {
  /**
   * @param {OmneoClient} client
   */
  constructor(client) {
    /** @type {ID} */
    this.client = client?.id
  }

  /** PRIVATE METHODS */

  /**
   * Base Url
   * @returns {string}
   */
  #getBaseUrl() {
    return `${GM_STATE.omneo.apiBaseUrl}/api`
  }

  /**
   * Bearer Token for Profile API
   * @returns {string}
   */
  #getToken() {
    return this.client?.IDToken
  }

  /**
   * Headers for Profile API
   *
   * @returns {{headers: {[key: string]: string}}}
   */
  #getHeaders() {
    return {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.#getToken()}`
      }
    }
  }

  statusRedirects(code) {
    return {
      401: () => {
        window.location.href = '/account/logout#account'
      },
      500: () => {}
    }[code]()
  }

  /**
   * PRIVATE METHOD
   * Fetch Client
   *
   * @param {{endpoint: string, isJson?: boolean, returnRawResponse?: boolean, options: {body?: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' }}} param0
   * @returns {Promise<{data: any, message: string}>}
   */
  async fetchClient({ endpoint, options, isJson = true, returnRawResponse = false }) {
    const response = await fetch(this.#getBaseUrl().concat(endpoint), { ...options, ...this.#getHeaders() })
    if (!response.ok) {
      // this.statusRedirects(response.status)
      const responseData = await response?.json()
      const { message } = responseData

      return {
        data: null,
        message
      }
    }
    if (!isJson) {
      return {
        data: null,
        message: 'success'
      }
    }
    const json = await response.json()

    if (returnRawResponse) {
      return { data: json, message: 'success' }
    }

    return { data: json.data, message: 'success' }
  }

  /** END PRIVATE METHODS */

  /**
   * Returns the base Profile.
   *
   * Method: GET
   *
   * @returns {Promise<Profile>}
   */
  async get() {
    const response = await this.fetchClient({
      endpoint: '/profile',
      options: { method: 'GET' }
    })
    return response?.data
  }

  /**
   *
   * Updates and Returns the base Profile.
   *
   * Method: PUT
   *
   * @param {Profile} body
   * @returns {Promise<{data:Profile, message: string}>}
   */
  async update(body) {
    const response = await this.fetchClient({
      endpoint: '/profile',
      options: { method: 'PUT', body: JSON.stringify(body) }
    })
    return response
  }

  /**
   *
   * Updates the Profiles's preferred communication channels (Email / SMS)
   *
   * Method: PUT
   *
   * @param {*} communicationPayload
   */
  async updateCommunicationPreferences(communicationPayload) {
    const response = await this.fetchClient({
      endpoint: '/profile/attributes/comms',
      options: {
        method: 'PUT',
        body: JSON.stringify(communicationPayload)
      }
    })
    return response
  }

  async optOutAllNonEssentialCommunication() {}

  /**
   * Returns array of Orders (Shopify Order Object)
   *
   * @returns {Promise<Order>}
   */
  async getOrders() {
    const orders = await fetch(`${this.#getBaseUrl()}/orders?idToken=${this.#getToken()}&source=shopify`)
    const data = await orders.json()
    return data
  }

  /**
   *
   * Returns a single Order based off the externalId (Shopify Order Id)
   *
   * @param {string} externalId
   * @returns {Promise<Order>}
   */
  async getOrdersById(externalId) {
    const order = await fetch(`${this.#getBaseUrl()}/orders/${externalId}?idToken=${this.#getToken()}&source=shopify`)
    const data = await order.json()
    return data
  }

  /**
   * Returns a array of Child Profile Connections.
   * To obtain profile information of the Child profile use the
   * Client provided outside of the ProfileClient Context.
   *
   * Method: GET
   *
   * @return {Promise<Connection[]>}
   */
  async getChildren() {
    const response = await this.fetchClient({
      endpoint: '/profile/connections',
      options: {
        method: 'GET'
      }
    })
    return response.data
  }

  /**
   *
   * Used in the onBoarding and creating a new child profile section.
   *
   * Creates and returns a new Profile that is available by calling the
   * getConnections method.
   *
   * Method: POST
   *
   * @param {createProfileConnection} payload
   * @returns {Promise<Connection>}
   */
  async createDependantProfileConnection(payload) {
    const customAttributes = []
    if (payload?.brands?.length) {
      customAttributes.push({
        namespace: 'omneo',
        type: 'array',
        handle: 'brands',
        value: payload?.brands
      })
    }

    if (payload?.sports?.length) {
      customAttributes.push({
        namespace: 'omneo',
        type: 'array',
        handle: 'sports',
        value: payload?.sports
      })
    }
    const body = {
      profile: {
        gender: payload.gender,
        avatar_url: payload.avatarUrl,
        identities: [
          {
            handle: 'shoetag',
            identifier: payload.shoeTag
          }
        ],
        first_name: payload.firstName,
        last_name: payload.lastName,
        custom_attributes: customAttributes,
        profile_type: 'dependant',
        birth_day: payload.birthYear ? 1 : null, // Required by the API but we don't want to use it for child profiles so we set it to 1
        birth_month: payload.birthMonth,
        birth_year: payload.birthYear
      },
      connection: {
        connection_definition_id: 1,
        name: 'Child',
        status: 'accepted'
      }
    }

    /** @type {createProfileConnectionResponse} */
    const response = await this.fetchClient({
      endpoint: '/connections',
      options: {
        method: 'POST',
        body: JSON.stringify(body)
      },
      returnRawResponse: true
    })

    if (!response?.data || response.data?.status !== 201) {
      throw new Error(response?.data?.message || 'Failed to create child profile connection')
    }

    const { data } = response.data.body
    return data
  }

  /**
   * Returns the wishlist's data for main logged in Profile.
   *
   * Method: GET
   *
   * @returns {Promise<List[]>}
   */
  async getWishlists() {
    const response = await this.fetchClient({
      endpoint: '/profile/lists',
      options: {
        method: 'GET'
      }
    })
    return response.data
  }

  /**
   * Create and returns a new Wishlist.
   *
   * Method: POST
   *
   * @param {string} name
   * @returns {Promise<List>}
   */
  async createWishlist(name) {
    const response = await this.fetchClient({
      endpoint: '/profile/lists',
      options: {
        method: 'POST',
        body: JSON.stringify({ name })
      }
    })
    return response.data
  }

  /**
   * Returns a single wishlist entity
   *
   * Method: GET
   *
   * @param {string} id
   * @returns {Promise<List>}
   */
  async getWishlistById(id) {
    const response = await this.fetchClient({
      endpoint: `/profile/lists/${id}`,
      options: {
        method: 'GET'
      }
    })
    return response.data
  }

  /**
   * Returns a single wishlist entity
   *
   * Method: GET
   *
   * @param {string} id
   * @param {{name: string}} body
   * @returns {Promise<List>}
   */
  async updateWishlistById(id, body) {
    const response = await this.fetchClient({
      endpoint: `/profile/lists/${id}`,
      options: {
        method: 'PUT',
        body: JSON.stringify(body)
      }
    })
    return response.data
  }

  /**
   * Deletes a Wishlist
   *
   * Method: DELETE
   *
   * @param {string} id
   * @returns {Promise<void>}
   */
  async removeWishlistById(id) {
    await this.fetchClient({
      endpoint: `/profile/lists/${id}`,
      options: {
        method: 'DELETE'
      },
      isJson: false
    })
  }

  /**
   *
   * Adds a product variant to a Wishlist
   *
   * Method: POST
   *
   * @param {string} id
   * @param {number} sku
   * @param {{img: string, selected: number}} meta
   * @returns {Promise<List>}
   */
  async addWishlistItem(id, sku, meta) {
    const response = await this.fetchClient({
      endpoint: `/profile/lists/${id}/items`,
      options: {
        method: 'POST',
        body: JSON.stringify({
          product_variant_sku: sku,
          meta
        })
      }
    })
    return response.data
  }

  /**
   * Updates a single list item from a wishlist
   *
   * Method: PUT
   *
   * @param {string} listId
   * @param {string} listItemId
   * @param {*} body
   */
  async updateWishlistItem(listId, listItemId, body) {
    const response = await this.fetchClient({
      endpoint: `/profile/lists/${listId}/items/${listItemId}`,
      options: {
        method: 'PUT',
        body: JSON.stringify(body)
      }
    })
    return response.data
  }

  /**
   * Once a profile has been created and the customer is directed to the
   * AccountDashboard page (index) the customer's non-logged in wishlist
   * from local-storage should be synced over to a new Omneo List for that
   * customer's profile.
   *
   * We have to sync each item one at a time to ensure that if one item is not
   * valid or avalaible on Omneo that the whole process does not fail.
   *
   * Once successfully synced to Omneo the customers local-storage wishslist data
   * will be removed and the customers profile will be given a custom attribute tag
   *
   * wishlist_synced = true
   *
   *
   * @param {Profile} profile
   * @returns {Promise<void>}
   */
  async syncMyWishlist(profile) {
    const list = window.localStorage.getItem('customer-wishlist-SS')
    if (!list) {
      /**
       * TODO
       * Need to update the profile with a custom attribute to indicate that the
       * customers wishlist has been synced.
       */
      //   if (!profile?.meta?.wishlist_synced) {
      //     /** replace with customer_attribute */
      //     await this.update({
      //       meta: {
      //         ...profile?.meta,
      //         wishlist_synced: true
      //       }
      //     })
      //   }

      return
    }
    /** @type {string[]} */
    const productData = JSON.parse(list)

    /**
     * @type {{[key: string]: string[]}}
     */
    const productDataMap = productData.reduce((acc, item) => {
      const [product, variant] = item.split(':')
      if (!variant) return acc
      if (acc[product]) {
        acc[product].push(variant)
        return acc
      }
      acc[product] = [variant]
      return acc
    }, {})
    /** @type {string[]} */
    const productIds = Object.keys(productDataMap)
    const data = new OmneoWishlistParser([])
    const items = await data.handleProductWishlistParse(productIds, productDataMap)

    const wishlist = await this.createWishlist('My First Wishlist')
    if (!wishlist?.id) {
      console.error('Looks like something went wrong')
      return
    }
    const id = wishlist.id
    for (const item of items) {
      await asyncTimeout(1000)
      await this.addWishlistItem(id, item.sku, {
        productId: item.productId,
        variantId: item.variantId,
        img: item.img,
        selected: item.sku
      })
    }

    /** replace with customer_attribute */
    await this.update({
      meta: {
        ...profile.meta,
        wishlist_synced: true
      }
    })

    /** Remove customer-wishlist from local storage */
    window.localStorage.removeItem('customer-wishlist-SS')
  }

  /**
   *
   * Returns a single list item from list
   *
   * Method: GET
   *
   * @param {string} id
   * @param {string} itemId
   * @returns {Promise<ListItem>}
   */
  async getWishlistItemById(id, itemId) {
    const response = await this.fetchClient({
      endpoint: `/profile/lists/${id}/items/${itemId}`,
      options: {
        method: 'GET'
      }
    })
    return response.data
  }

  /**
   *
   * Removes a single list item from list
   *
   * Method: GET
   *
   * @param {string} id
   * @param {string} itemId
   * @returns {Promise<void>}
   */
  async removeWishlistItem(id, itemId) {
    await this.fetchClient({
      endpoint: `/profile/lists/${id}/items/${itemId}`,
      options: {
        method: 'DELETE'
      },
      isJson: false
    })
  }

  /**
   * Returns a array of Address values
   *
   * Method: GET
   *
   * @returns {Promise<Address[]>}
   */
  async getAddresses() {
    const response = await this.fetchClient({
      endpoint: '/profile/addresses',
      options: {
        method: 'GET'
      }
    })
    return response.data
  }

  /**
   *
   * Creates new address for base Profile.
   *
   * Method: POST
   *
   * @param {*} body
   * @returns
   */
  async createAddress(body) {
    const response = await this.fetchClient({
      endpoint: '/profile/addresses',
      options: {
        method: 'POST',
        body: JSON.stringify(body)
      }
    })
    return response.data
  }

  /**
   * Updates the address
   *
   * Method: PUT
   *
   * @param {string} id
   * @param {*} body
   */
  async updateAddressById(id, body) {
    const response = await this.fetchClient({
      endpoint: `/profile/addresses/${id}`,
      options: {
        method: 'PUT',
        body: JSON.stringify(body)
      }
    })
    return response.data
  }

  /**
   *
   * Deletes address from Profile.
   *
   * Method: DELETE
   *
   * @param {string} id
   * @returns {Promise<void>}
   */
  async deleteAddressById(id) {
    await this.fetchClient({
      endpoint: `/profile/addresses/${id}`,
      options: {
        method: 'DELETE'
      },
      isJson: false
    })
  }

  /**
   * Returns base profile rewards
   *
   * Method: GET
   *
   * @returns {Promise<Reward[]>}
   */
  async getRewards() {
    const response = await this.fetchClient({
      endpoint: '/profile/rewards',
      options: {
        method: 'GET'
      }
    })

    return response.data
  }

  /**
   * Returns base profile available balance
   *
   * Method: GET
   *
   * @returns {Promise<ProfileBalances>}
   */
  async getBalance() {
    const response = await this.fetchClient({
      endpoint: '/profile/balances',
      options: {
        method: 'GET'
      }
    })

    return response.data
  }

  /**
   *
   * Returns an array of the customer's TransactionLedger
   *
   * Method: GET
   *
   * @returns {Promise<TransactionLedger[]>}
   */
  async getTransactions() {
    return await this.fetchClient({
      endpoint: '/profile/transactions?include=product&page%5Bnumber%5D=1&page%5Bsize%5D=100&sort=-transacted_at',
      options: {
        method: 'GET'
      }
    })
  }

  /**
   *
   * Returns a transaction by Id
   *
   * Method: GET
   *
   * @param {number} id
   * @returns {Promise<Transaction>}
   */
  async getTransactionsById(id) {
    const response = await this.fetchClient({
      endpoint: `/profile/transactions/${id}?include=product&include_list_item=1`,
      options: {
        method: 'GET'
      }
    })
    return response.data
  }

  /**
   *
   * Returns an array of the customer's interactions
   *
   * Method: GET
   *
   * @returns {Promise<Interaction[]>}
   */
  async getInteractions() {
    return await this.fetchClient({
      endpoint: '/profile/interactions',
      options: {
        method: 'GET'
      }
    })
  }

  /**
   *
   * Method: GET
   *
   * @param {'orders' | 'transactions'} type
   * @returns
   */
  async getLedgers(type) {
    const ledgers = await this.fetchClient({
      endpoint: '/profile/ledgers',
      options: {
        method: 'GET'
      }
    })

    if (type) {
      return ledgers.data.filter((ledger) => ledger.type === type)
    }

    return ledgers?.data
  }

  /**
   * Calls the getLedgers method passing it the 'orders' search param key.
   * This will return an array of OrderLedges.
   *
   * METHOD: GET
   *
   * @returns {Promise<OrderLedger[]>}
   */
  async getLedgerOrders() {
    return await this.getLedgers()
  }

  /**
   *
   * @returns {Promise<Transaction[]>}
   */
  async getTransactionsQuery() {
    const res = await this.fetchClient({
      endpoint: '/profile/transactions?include=product,product_images&sort=-transacted_at',
      options: {
        method: 'GET'
      }
    })
    return res.data
  }

  /**
   * Returns a list of the profiles unassigned transactions items
   * Method: Get
   *
   * @returns {Promise<TransactionItem[]>}
   */
  async getUnassignedTransactionItems(filterOutExpired = true) {
    const response = await this.fetchClient({
      endpoint:
        '/profile/transactionitems/list/unassigned?include_list_item=1&include=product&page%5Bsize%5D=15&sort=-created_at',
      options: {
        method: 'GET'
      }
    })

    if (response?.data?.length && filterOutExpired) {
      return response.data.filter((item) => getRemainingDaysOnAssignment(item.created_at) > 0)
    }

    return response.data
  }

  /**
   * @typedef {Object} Link
   * @property {string|null} url
   * @property {string} label
   * @property {boolean} active
   */
  /**
   * @typedef {Object} PaginationMeta
   * @property {number} current_page
   * @property {number} from
   * @property {number} last_page
   * @property {Link[]} links
   * @property {string} path
   * @property {number} per_page
   * @property {number} to
   * @property {number} total
   */
  /**
   * CHILDREN METHODS
   *
   * The SDK has some methods that have not been updated to handle recent
   */

  /**
   *
   * Gets all measurement records for a child profile
   *
   * Method: GET
   *
   * @param {number} connectionId
   * @return {Promise<Interaction[]>}
   */
  async getChildMeasurements(connectionId, pageNumber = 1, perPage = 15) {
    /** @type {{data: Interaction[], meta: PaginationMeta}} */
    const { data } = await this.fetchClient({
      endpoint: `/connections/${connectionId}/interactions?name=Measurement Recorded&page[number]=${pageNumber}&page[size]=${perPage}&sort=-created_at`,
      options: {
        method: 'GET'
      },
      returnRawResponse: true
    })

    return { data: data.data, meta: data.meta }
  }

  /**
   *
   * Deletes a measurement recording
   *
   * Method: DELETE
   *
   * @param {string} connectionId
   * @param {string} interactionId
   * @returns {Promise<void>}
   */
  async deleteChildInteractionById(connectionId, interactionId) {
    await this.fetchClient({
      endpoint: `/connections/${connectionId}/interactions/${interactionId}`,
      options: {
        method: 'DELETE'
      },
      isJson: false
    })
  }

  /**
   *
   * Creates a measurement recording for a child profile
   *
   * Method: POST
   *
   * @param {number} connectionId
   * @param {any} body
   */
  async createChildProfileMeasurements(connectionId, body) {
    const response = await this.fetchClient({
      endpoint: `/connections/${connectionId}/interactions`,
      options: {
        method: 'POST',
        body: JSON.stringify(body)
      }
    })

    const { data } = response
    return data
  }

  /**
   *
   * Links a transaction line item to a profiles assigned list.
   *
   * Method: POST
   *
   * @param {number} itemId
   * @param {{product_list_item_id: number, type: 'link'}} payload
   */
  async linkTransactionToList(itemId, payload) {
    const response = await this.fetchClient({
      endpoint: `/profile/transactions/items/${itemId}/list-item`,
      options: {
        method: 'POST',
        body: JSON.stringify(payload)
      }
    })
    return response
  }

  /**
   *
   * Returns a list of store locations relative to the customers lat & lng coordinates
   *
   * Method: GET
   *
   * @returns {Promise<Location[]>}
   */
  async getStores() {
    const response = await this.fetchClient({
      endpoint: '/geolocation',
      returnRawResponse: true,
      options: {
        method: 'GET'
      }
    })

    const locationData = response?.data?.locations ?? {}
    const locations = Object.values(locationData)
    if (!locations?.length) return []
    return locations
  }

  /**
   *
   * Submits a transaction not yet claimed.
   *
   * Method: POST
   * Example payload
   *
   *
   * { "transaction_transacted_at": "2025-01-05",
   * "transaction_receipt_ref": "12341",
   * "transaction_total": "88",
   * "transaction_location_external_code": 46,
   * "transaction_timezone": "Australia/Sydney",
   * "profile_id": UUID
   *  }
   *
   * @param {{profile_id: string, transaction_location_external_code: string, transaction_receipt_ref: string, transaction_timezone: string, transaction_total: string, transaction_transacted_at}} payload
   * @returns {Promise<Transaction>}
   */
  async claimTransaction(payload) {
    const response = await this.fetchClient({
      endpoint: '/profile/transactions/claims',
      options: {
        method: 'POST',
        body: JSON.stringify(payload)
      }
    })
    return response?.data
  }

  /** Page Based Methods */

  /**
   *
   * @returns {Promise<DashboardPage>}
   */
  async getDashboardData() {
    const profile = await this.get()
    const connections = await this.getChildren()
    const balance = await this.getBalance()
    await this.syncMyWishlist(profile)
    return {
      profile,
      connections,
      balance
    }
  }

  async getFitClubPageData() {}
  /** End Page Based Methods */
}
